import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { pyFetch } from '@/utils/backend';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: string;
  email_verified: boolean;
  phone_number?: string;
  profile_image_url?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string, expectedRole?: string) => Promise<{ error?: any; success?: boolean; user?: any }>;
  devSignIn?: (email: string, password: string) => Promise<{ error?: any; success?: boolean; user?: any }>;
  signUp: (email: string, password: string, userData: any) => Promise<{ error?: any; success?: boolean }>;
  signOut: () => Promise<void>;
  getUserProfile: (force?: boolean) => Promise<any>;
  updateProfileImage: (imageUrl: string) => Promise<void>;
  sendOTP: (email: string, action: string) => Promise<{ success: boolean; error?: string; otp?: string }>;
  verifyOTP: (email: string, otp: string, action: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const profileFetchRef = useRef<Promise<any> | null>(null);
  const hasCheckedAuthRef = useRef<boolean>(false);

  useEffect(() => {
    // Only check auth status once on mount
    if (!hasCheckedAuthRef.current) {
      hasCheckedAuthRef.current = true;
      checkAuthStatus();
    }
  }, []);

  const checkAuthStatus = async () => {
    // Prevent multiple simultaneous calls
    if (profileFetchRef.current) {
      try {
        // Wait for existing fetch with a shorter timeout (2 seconds)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Wait timeout')), 2000)
        );
        const existing = await Promise.race([profileFetchRef.current, timeoutPromise]);
        if (existing && existing.id) {
          setUser(existing);
          setLoading(false);
        } else {
          setUser(null);
          setLoading(false);
        }
      } catch (e: any) {
        // If waiting failed, try to use cached profile
        try {
          const cached = sessionStorage.getItem('auth_profile');
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.id) {
              setUser(parsed);
              setLoading(false);
              return;
            }
          }
        } catch (cacheErr) {
          // Ignore cache errors
        }
        setUser(null);
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      
      // Try to restore from sessionStorage first (fast - no network call)
      // Also check localStorage as backup for persistence across sessions
      try {
        let cached = sessionStorage.getItem('auth_profile');
        let hasToken = sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
        
        // If not in sessionStorage, try localStorage (for persistence across browser sessions)
        if (!cached) {
          try {
            cached = localStorage.getItem('auth_profile');
          } catch (e) {
            // Ignore localStorage errors
          }
        }
        
        if (cached && hasToken) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.id) {
            setUser(parsed);
            setLoading(false);
            // Verify with backend in background (non-blocking) - don't wait
            getUserProfile(false).catch(() => {
              // Ignore errors - we have cached data, user stays logged in
            });
            return;
          }
        }
      } catch (e) {
        // ignore storage errors
      }

      // Try to get token from storage
      let authToken = null;
      try {
        authToken = sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
      } catch (e) {
        // Ignore storage errors
      }
      
      // If no token found, check localStorage as backup (for persistence across sessions)
      if (!authToken) {
        // Double-check localStorage (might have been set there)
        try {
          authToken = localStorage.getItem('auth_token');
          if (authToken) {
            // Token found in localStorage, continue with auth check
          } else {
            setUser(null);
            setLoading(false);
            return;
          }
        } catch (e) {
          setUser(null);
          setLoading(false);
          return;
        }
      }
      
      // Make request with timeout
      const fetchOptions: any = { method: 'GET', useApiKey: false };
      if (authToken) {
        fetchOptions.headers = { Authorization: `Bearer ${authToken}` };
      }

      try {
        // Don't add extra timeout - pyFetch already has timeout handling
        profileFetchRef.current = pyFetch('/api/auth/me', fetchOptions);
        const profile = await profileFetchRef.current;
        profileFetchRef.current = null;

        if (profile && profile.id) {
          // Store token if it's in the response (backend now returns it)
          if (profile.token) {
            try {
              sessionStorage.setItem('auth_token', profile.token);
              localStorage.setItem('auth_token', profile.token);
              // Remove token from profile object before storing
              const { token, ...profileWithoutToken } = profile;
              setUser(profileWithoutToken);
              try { 
                sessionStorage.setItem('auth_profile', JSON.stringify(profileWithoutToken)); 
                localStorage.setItem('auth_profile', JSON.stringify(profileWithoutToken)); // Persist to localStorage
              } catch (e) {}
            } catch (e) {
              setUser(profile);
              try { 
                sessionStorage.setItem('auth_profile', JSON.stringify(profile)); 
                localStorage.setItem('auth_profile', JSON.stringify(profile)); // Persist to localStorage
              } catch (e) {}
            }
          } else {
            setUser(profile);
            try { 
              sessionStorage.setItem('auth_profile', JSON.stringify(profile)); 
              localStorage.setItem('auth_profile', JSON.stringify(profile)); // Persist to localStorage
            } catch (e) {}
          }
        } else {
          setUser(null);
        }
      } catch (error: any) {
        // Clear the ref immediately to prevent other calls from waiting
        profileFetchRef.current = null;
        const errorMsg = error?.message || String(error);
        const isAuthError = error?.status === 401 || 
                           error?.isAuthError ||
                           errorMsg.includes('401') || 
                           errorMsg.includes('Unauthorized') || 
                           errorMsg.includes('Authentication required');
        
        // If it's a 401, user is not authenticated - BUT check cached data first
        // This prevents logout on refresh when API is temporarily unavailable
        if (isAuthError) {
          // ALWAYS check cached data first - might be a temporary API issue
          try {
            let cached = sessionStorage.getItem('auth_profile');
            // Also check localStorage for persistence across sessions
            if (!cached) {
              cached = localStorage.getItem('auth_profile');
            }
            const hasToken = sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
            // If we have cached profile and token, keep user logged in (API might be down)
            if (cached && hasToken) {
              try {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.id) {
                  setUser(parsed);
                  setLoading(false);
                  // Try to refresh profile in background (non-blocking)
                  getUserProfile(false).catch(() => {
                    // Ignore errors - we have cached data
                  });
                  return;
                }
              } catch (parseError) {
                // Invalid cached data, continue to clear
              }
            }
          } catch (e) {
            // Ignore cache errors
          }
          
          // No valid cached data - clear user
          setUser(null);
          try {
            sessionStorage.removeItem('auth_profile');
            sessionStorage.removeItem('auth_token');
            localStorage.removeItem('auth_profile');
            localStorage.removeItem('auth_token');
          } catch (e) {
            // Ignore
          }
          setLoading(false);
          return;
        } else {
          // Other errors (timeout, network issues, connection refused, etc.) - ALWAYS use cached profile if available
          // This prevents logout on refresh when there are network issues
          const isConnectionError = errorMsg.includes('Connection refused') || 
                                   errorMsg.includes('ERR_CONNECTION') || 
                                   errorMsg.includes('NetworkError') ||
                                   errorMsg.includes('timeout') ||
                                   errorMsg.includes('Request timeout') ||
                                   errorMsg.includes('Failed to fetch');
          
          // For ANY error (not just connection errors), try to use cached profile first
          // This ensures users stay logged in on refresh even if API is temporarily unavailable
          try {
            let cached = sessionStorage.getItem('auth_profile');
            // Also check localStorage for persistence across sessions
            if (!cached) {
              cached = localStorage.getItem('auth_profile');
            }
            const hasToken = sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
            
            if (cached && hasToken) {
              const parsed = JSON.parse(cached);
              if (parsed && parsed.id) {
                console.log('[AUTH] API error but keeping cached user - using cached profile:', isConnectionError ? 'connection error' : 'other error');
                setUser(parsed);
                setLoading(false);
                // Sync to sessionStorage if it came from localStorage
                if (!sessionStorage.getItem('auth_profile')) {
                  try {
                    sessionStorage.setItem('auth_profile', cached);
                    if (localStorage.getItem('auth_token')) {
                      sessionStorage.setItem('auth_token', localStorage.getItem('auth_token') || '');
                    }
                  } catch (e) {
                    // Ignore sync errors
                  }
                }
                // Try to refresh profile in background (non-blocking)
                getUserProfile(false).catch(() => {
                  // Ignore errors - we have cached data
                });
                return;
              }
            }
          } catch (e) {
            // Ignore cache errors
          }
          
          // No cached profile available - only then clear user
          // This should rarely happen if user was previously logged in
          if (!isConnectionError) {
            console.warn('[AUTH] Non-connection error and no cached profile - clearing user');
          }
          setUser(null);
        }
      } finally {
        // Always clear the ref and set loading to false
        profileFetchRef.current = null;
        setLoading(false);
      }
    } catch (error) {
      setUser(null);
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string, expectedRole?: string) => {
    try {
      const response = await pyFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.toLowerCase(), password }),
        useApiKey: false
      });

      if (response.success && response.user) {
        // Validate role if expectedRole is provided
        if (expectedRole) {
          const userRole = response.user.user_type?.toLowerCase();
          const roleNames: Record<string, string> = {
            'agent': 'Agent',
            'seller': 'Seller',
            'buyer': 'Buyer',
            'admin': 'Administrator'
          };
          
          // Handle special case for buyer_or_seller
          if (expectedRole.toLowerCase() === 'buyer_or_seller') {
            if (userRole !== 'buyer' && userRole !== 'seller') {
              const actualRoleName = roleNames[userRole] || userRole;
            return {
              error: `This account is registered as ${actualRoleName}. Please use the ${actualRoleName} login page.`,
              success: false
            };
            }
          } else if (userRole !== expectedRole.toLowerCase()) {
            const expectedRoleName = roleNames[expectedRole.toLowerCase()] || expectedRole;
            const actualRoleName = roleNames[userRole] || userRole;
            
            return {
              error: `This account is registered as ${actualRoleName}, not ${expectedRoleName}. Please use the ${actualRoleName} login page.`,
              success: false
            };
          }
        }
        
        // If the backend returned an access token, use it to hydrate the profile
        // immediately via Authorization header so the app does not rely solely
        // on cookies (which can be blocked in dev due to the Secure flag).
        let hydrated: any = undefined;
        try {
          const authToken = response.token || response.access_token || response.accessToken;
          if (authToken) {
            // Store token in both sessionStorage and localStorage for persistence
            // sessionStorage for current session, localStorage as backup
            try { 
              sessionStorage.setItem('auth_token', authToken);
              localStorage.setItem('auth_token', authToken);
            } catch (e) {
              // Ignore storage errors
            }
            
            hydrated = await pyFetch('/api/auth/me', {
              method: 'GET',
              headers: { Authorization: `Bearer ${authToken}` },
              useApiKey: false
            });
            if (hydrated && hydrated.id) {
              setUser(hydrated);
              try { sessionStorage.setItem('auth_profile', JSON.stringify(hydrated)); } catch (e) {}
            } else {
              // Fallback to returned user object
              setUser(response.user);
              try { sessionStorage.setItem('auth_profile', JSON.stringify(response.user)); } catch (e) {}
            }
          } else {
            setUser(response.user);
            // Try to get token from cookie as fallback
            try {
              const cookies = document.cookie.split(';');
              for (const cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === 'auth_token' && value) {
                    try {
                      sessionStorage.setItem('auth_token', value);
                      localStorage.setItem('auth_token', value);
                    } catch (e) {
                      // Ignore storage errors
                    }
                  break;
                }
              }
            } catch (e) {
              // Ignore cookie errors
            }
          }
        } catch (hydrateErr) {
          setUser(response.user);
          try { sessionStorage.setItem('auth_profile', JSON.stringify(response.user)); } catch (e) {}
          // Try to get token from cookie as fallback
          try {
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
              const [name, value] = cookie.trim().split('=');
              if (name === 'auth_token' && value) {
                try {
                  sessionStorage.setItem('auth_token', value);
                  localStorage.setItem('auth_token', value);
                } catch (e) {
                  // Ignore storage errors
                }
                break;
              }
            }
          } catch (e) {
            // Ignore cookie errors
          }
        }

        toast.success('Welcome back!');
        // Return the hydrated user (if available) so callers get the freshest profile
        const returnedUser = (hydrated && hydrated.id) ? hydrated : response.user;
        // Ensure sessionStorage reflects the returned user
        try { sessionStorage.setItem('auth_profile', JSON.stringify(returnedUser)); } catch (e) {}
        // Return the user object so callers don't have to rely on immediate state update
        return { success: true, user: returnedUser };
      } else {
        return { error: response.error || 'Invalid credentials' };
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Sign in failed';
      
      // Silent error handling for production
      
      if (errorMessage.includes('pending admin approval')) {
        return { 
          error: 'approval_pending',
          message: 'Your account is pending admin approval. Please wait for admin approval before you can login.',
          email: email,
          userType: 'user'
        };
      }
      if (errorMessage.includes('Email not verified')) {
        return { error: 'Please verify your email before signing in. Check your inbox for verification link.' };
      }
      if (errorMessage.includes('pending verification') || errorMessage.includes('not verified')) {
        return { error: 'Your account is pending verification. Please wait for admin approval or contact support.' };
      }
      if (errorMessage.includes('not active')) {
        return { error: 'Your account is not active. Please contact support for assistance.' };
      }
      if (errorMessage.includes('Invalid email or password')) {
        return { error: 'Invalid email or password. Please check your credentials.' };
      }
      
      return { error: errorMessage };
    }
  };

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      console.log('[AUTH] Signing up:', email, 'as', userData.userType);
      
      // Build signup payload with all available user data
      const signupPayload: any = {
        email: email.toLowerCase(),
        password,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.userType || 'buyer',
        user_type: userData.userType || 'buyer', // Ensure user_type is set
        phone_number: userData.phone_number,
        city: userData.city,
        state: userData.state
      };
      
      // Add optional fields if they exist (for agents and sellers)
      if (userData.date_of_birth) signupPayload.date_of_birth = userData.date_of_birth;
      if (userData.district) signupPayload.district = userData.district;
      if (userData.mandal) signupPayload.mandal = userData.mandal;
      if (userData.zip_code) signupPayload.zip_code = userData.zip_code;
      if (userData.address) signupPayload.address = userData.address;
      if (userData.latitude) signupPayload.latitude = userData.latitude;
      if (userData.longitude) signupPayload.longitude = userData.longitude;
      if (userData.experience_years) signupPayload.experience_years = userData.experience_years;
      if (userData.specialization) signupPayload.specialization = userData.specialization;
      if (userData.business_name) signupPayload.business_name = userData.business_name;
      if (userData.business_type) signupPayload.business_type = userData.business_type;
      
      const response = await pyFetch('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify(signupPayload),
        useApiKey: false
      });

      if (response.success) {
        console.log('[AUTH] Sign up successful, user_type:', userData.userType);
        // Don't auto-login after signup - require email verification first
        setUser(null);
        const userTypeMessage = userData.userType === 'agent' 
          ? 'Agent account created! Please check your email to verify your account.'
          : userData.userType === 'seller'
          ? 'Seller account created! Please check your email to verify your account.'
          : 'Account created! Please check your email to verify your account.';
        toast.success(userTypeMessage);
        return { success: true, userType: userData.userType };
      } else {
        return { error: response.error || 'Signup failed' };
      }
    } catch (error: any) {
      console.error('[AUTH] Sign up error:', error);
      const errorMessage = error.message || 'Signup failed. Please try again.';
      
      if (errorMessage.includes('already exists') || errorMessage.includes('already registered')) {
        return { error: 'An account with this email already exists. Please sign in or use forgot password.' };
      }
      
      return { error: errorMessage };
    }
  };

  const signOut = async () => {
    // Clear user state immediately (don't wait for API)
    setUser(null);
    try { 
      sessionStorage.removeItem('auth_profile');
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token');
    } catch (e) {
      // Ignore storage errors
    }
    
    // Call logout API in background (non-blocking) - don't wait for it
    pyFetch('/api/auth/logout', { method: 'POST', useApiKey: false })
      .catch(() => {
        // Ignore errors - we've already cleared local state
      });
    
    toast.success('Signed out successfully');
    
    // Scroll to top immediately
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Development-only helper to allow hardcoded admin login without calling the backend.
  const devSignIn = async (email: string, password: string) => {
    try {
      // WARNING: This is a development-only shortcut. Do NOT enable in production.
      const DEV_ADMIN_EMAIL = 'admin@homeandown.com';
      const DEV_ADMIN_PASSWORD = 'Frisco@2025';
      if (email.toLowerCase() === DEV_ADMIN_EMAIL && password === DEV_ADMIN_PASSWORD) {
        console.warn('[AUTH][DEV] Using hardcoded dev admin sign-in. This should only run in development.');
        
        // Try to fetch the real admin user from the database to get a valid UUID
        try {
          // Try to get admin user by email from the backend using API key
          const adminUsers = await pyFetch('/api/admin/users', {
            method: 'GET',
            useApiKey: true,
          });
          
          // Find the admin user with matching email
          const adminUser = Array.isArray(adminUsers) 
            ? adminUsers.find((u: any) => u.email?.toLowerCase() === DEV_ADMIN_EMAIL)
            : null;
          
          if (adminUser && adminUser.id) {
            console.log('[AUTH][DEV] Found real admin user in database:', adminUser.id);
            const devUser = {
              id: adminUser.id, // Use real UUID from database
              email: adminUser.email || DEV_ADMIN_EMAIL,
              first_name: adminUser.first_name || 'Admin',
              last_name: adminUser.last_name || 'User',
              user_type: 'admin',
              email_verified: true,
            };
            setUser(devUser as any);
            try { sessionStorage.setItem('auth_profile', JSON.stringify(devUser)); } catch (e) {}
            return { success: true, user: devUser };
          } else {
            console.warn('[AUTH][DEV] Admin user not found in database. Make sure admin@homeandown.com exists.');
          }
        } catch (fetchError) {
          console.warn('[AUTH][DEV] Could not fetch real admin user from database:', fetchError);
          console.warn('[AUTH][DEV] Falling back to dev-admin ID. Some features may not work.');
          // Fall through to use dev-admin ID
        }
        
        // Fallback: Use dev-admin ID if we can't fetch from database
        console.warn('[AUTH][DEV] Using fallback dev-admin ID. Some features may not work.');
        const devUser = {
          id: 'dev-admin',
          email: DEV_ADMIN_EMAIL,
          first_name: 'Dev',
          last_name: 'Admin',
          user_type: 'admin',
          email_verified: true,
        };
        setUser(devUser as any);
        try { sessionStorage.setItem('auth_profile', JSON.stringify(devUser)); } catch (e) {}
        return { success: true, user: devUser };
      }
      return { error: 'Dev credentials did not match' };
    } catch (error: any) {
      return { error: error.message || 'Dev sign-in failed' };
    }
  };

  /**
   * Returns the current user profile.
   * By default this returns the cached `user` value to avoid repeated network
   * requests. Pass `force=true` to always re-fetch from the backend.
   */
  const getUserProfile = async (force: boolean = false) => {
    try {
      // If we have a cached user and no forced refresh requested, return it.
      if (user && !force) return user;

      // If a fetch is already underway, reuse it.
      if (profileFetchRef.current) {
        const ongoing = await profileFetchRef.current;
        return ongoing || user;
      }

      // Try to get token from storage, but don't require it
      // The request will work with cookies (httponly cookies are sent automatically by browser)
      let authToken = null;
      try {
        authToken = sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
      } catch (e) {
        // Ignore storage errors
      }
      
      // Make request - if we have a token, use it; otherwise rely on cookies
      const fetchOptions: any = { method: 'GET', useApiKey: false };
      if (authToken) {
        fetchOptions.headers = { Authorization: `Bearer ${authToken}` };
        console.log('[AUTH] Using stored token for /api/auth/me request');
      } else {
        // No token in storage - make request with cookies only
        // httponly cookies are sent automatically by the browser
      }

      profileFetchRef.current = pyFetch('/api/auth/me', fetchOptions);
      // Add timeout to prevent hanging (reduced to 5 seconds to match backend timeout)
      const profile = await Promise.race([
        profileFetchRef.current,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Auth timeout')), 5000)) // 5 second timeout
      ]) as any;
      profileFetchRef.current = null;

      if (profile && profile.id) {
        // Store token if it's in the response (backend now returns it)
        if (profile.token) {
          try {
            sessionStorage.setItem('auth_token', profile.token);
            localStorage.setItem('auth_token', profile.token);
            // Remove token from profile object before storing
            const { token, ...profileWithoutToken } = profile;
            setUser(profileWithoutToken);
              try { 
                sessionStorage.setItem('auth_profile', JSON.stringify(profileWithoutToken)); 
                localStorage.setItem('auth_profile', JSON.stringify(profileWithoutToken)); // Persist to localStorage
              } catch (e) {}
            return profileWithoutToken;
          } catch (e) {
            setUser(profile);
            return profile;
          }
        } else {
          setUser(profile);
          return profile;
        }
      }
      return user;
    } catch (error: any) {
      // Don't log 401 errors as they're expected when user is not authenticated
      const errorMsg = error?.message || String(error);
      const isAuthError = error?.status === 401 || 
                         error?.isAuthError ||
                         errorMsg.includes('401') || 
                         errorMsg.includes('Unauthorized') || 
                         errorMsg.includes('Authentication required');
      
      if (!isAuthError && import.meta.env.DEV) {
        console.log('[AUTH] Get profile error (non-auth):', error);
      }
      profileFetchRef.current = null;
      // If 401, clear user to indicate session expired
      if (isAuthError) {
        // Don't clear user immediately - let the component handle it
        // This prevents unnecessary re-renders
      }
      return user;
    }
  };

  const sendOTP = async (email: string, action: string = "email_verification") => {
    try {
      console.log('[OTP] Sending OTP to:', email, 'for action:', action);
      
      const response = await pyFetch('/api/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email, action }),
        useApiKey: false
      });
      
      if (response.success) {
        console.log('[OTP] OTP sent successfully');
        toast.success('OTP sent to your email');
        return { 
          success: true, 
          otp: response.otp // Only in development mode
        };
      }
      
      return { success: false, error: 'Failed to send OTP' };
    } catch (error: any) {
      console.error('[OTP] Send OTP error:', error);
      return { success: false, error: error.message || 'Failed to send OTP' };
    }
  };

  const verifyOTP = async (email: string, otp: string, action: string = "email_verification") => {
    try {
      console.log('[OTP] Verifying OTP:', otp, 'for', email, 'action:', action);
      
      const response = await pyFetch('/api/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ email, otp, action }),
        useApiKey: false
      });
      
      if (response.success) {
        console.log('[OTP] OTP verified successfully');
        toast.success('OTP verified successfully');
        return { success: true };
      }
      
      return { success: false, error: 'Invalid OTP' };
    } catch (error: any) {
      console.error('[OTP] Verify OTP error:', error);
      return { success: false, error: error.message || 'Invalid OTP' };
    }
  };

  const updateProfileImage = async (imageUrl: string) => {
    try {
      console.log('[AUTH] Updating profile image...');
      
      const response = await pyFetch('/api/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({ profile_image_url: imageUrl }),
        useApiKey: false
      });

      if (response && response.id) {
        // Update local user state
        setUser(prev => prev ? { ...prev, profile_image_url: imageUrl } : null);
        try {
          const cached = sessionStorage.getItem('auth_profile');
          if (cached) {
            const parsed = JSON.parse(cached);
            parsed.profile_image_url = imageUrl;
            sessionStorage.setItem('auth_profile', JSON.stringify(parsed));
          }
        } catch (e) {
          // ignore sessionStorage errors
        }
        toast.success('Profile image updated successfully');
      }
    } catch (error: any) {
      console.error('[AUTH] Update profile image error:', error);
      toast.error('Failed to update profile image');
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signIn,
  devSignIn,
    signUp,
    signOut,
    getUserProfile,
    updateProfileImage,
    sendOTP,
    verifyOTP,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};